import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader, Card, StatusPill } from '@/components/Primitives';
import { TID } from '@/constants/testIds/voice';
import { ArrowsClockwise, Plus, X, PencilSimple, Trash } from '@phosphor-icons/react';
import { useToast } from '@/hooks/use-toast';

const blank = {
    number: '',
    country: '',
    city: '',
    label: '',
    customer: '',
    department: '',
    notes: '',
    is_active: true,
    assigned_call_flow_id: null,
};

const Dids = () => {
    const [items, setItems] = useState([]);
    const [flows, setFlows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const { toast } = useToast();

    const load = async () => {
        setLoading(true);
        const [r1, r2] = await Promise.all([api.get('/didww/dids'), api.get('/call-flows')]);
        setItems(r1.data);
        setFlows(r2.data);
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const sync = async () => {
        try {
            const r = await api.post('/didww/dids/sync');
            toast({ title: 'Sync OK', description: `Inserted ${r.data.inserted}, Updated ${r.data.updated}` });
            load();
        } catch (e) {
            toast({ title: 'Sync selhal', description: e.response?.data?.detail || 'chyba', variant: 'destructive' });
        }
    };

    const save = async (e) => {
        e.preventDefault();
        try {
            if (editing.id) {
                await api.patch(`/didww/dids/${editing.id}`, editing);
            } else {
                await api.post('/didww/dids', editing);
            }
            setEditing(null);
            load();
            toast({ title: 'Uloženo' });
        } catch (err) {
            toast({ title: 'Chyba', description: err.response?.data?.detail || 'error', variant: 'destructive' });
        }
    };

    const del = async (id) => {
        if (!window.confirm('Smazat toto DID číslo?')) return;
        await api.delete(`/didww/dids/${id}`);
        load();
    };

    return (
        <>
            <PageHeader
                eyebrow="Telephony"
                title="DID čísla"
                description="Veřejná telefonní čísla, která DIDWW směruje do vaší brány. Synchronizujte přímo z účtu DIDWW nebo přidejte ručně."
                actions={
                    <div className="flex gap-2">
                        <button
                            data-testid={TID.didwwSyncDids}
                            onClick={sync}
                            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.08em] border hover:bg-[var(--bg-surface-hover)]"
                            style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}
                        >
                            <ArrowsClockwise size={14} weight="bold" />
                            Sync DIDWW
                        </button>
                        <button
                            data-testid={TID.didCreateBtn}
                            onClick={() => setEditing({ ...blank })}
                            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.08em]"
                            style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}
                        >
                            <Plus size={14} weight="bold" />
                            Přidat DID
                        </button>
                    </div>
                }
            />

            <Card testId={TID.didTable}>
                {loading ? <div>Načítání…</div> : items.length === 0 ? (
                    <div className="py-8 text-center text-sm font-mono" style={{ color: 'var(--text-secondary)' }}>
                        Žádná DID čísla. Spusťte Sync DIDWW nebo přidejte ručně.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-[0.1em]"
                                style={{ color: 'var(--text-secondary)' }}>
                                <th className="text-left py-2">Číslo</th>
                                <th className="text-left">Země / Město</th>
                                <th className="text-left">Štítek / Zákazník</th>
                                <th className="text-left">Call Flow</th>
                                <th className="text-left">Zdroj</th>
                                <th className="text-center">Stav</th>
                                <th className="text-right">Akce</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((d) => (
                                <tr key={d.id} className="border-t"
                                    style={{ borderColor: 'var(--border-default)' }}>
                                    <td className="py-2 font-mono">{d.number}</td>
                                    <td className="font-mono text-xs">
                                        {(d.country || '—')} / {d.city || '—'}
                                    </td>
                                    <td className="text-xs">
                                        <div>{d.label || '—'}</div>
                                        <div style={{ color: 'var(--text-secondary)' }}>{d.customer || ''}</div>
                                    </td>
                                    <td className="font-mono text-xs">
                                        {(flows.find((f) => f.id === d.assigned_call_flow_id) || {}).name || '—'}
                                    </td>
                                    <td>
                                        <StatusPill status={d.source === 'didww' ? 'info' : 'neutral'}
                                                    label={d.source} />
                                    </td>
                                    <td className="text-center">
                                        <StatusPill status={d.is_active ? 'success' : 'neutral'}
                                                    label={d.is_active ? 'aktivní' : 'neaktivní'} />
                                    </td>
                                    <td className="text-right">
                                        <button onClick={() => setEditing({ ...d })}
                                                className="p-1 hover:bg-[var(--bg-surface-hover)]"
                                                data-testid={`did-edit-${d.id}`}>
                                            <PencilSimple size={14} />
                                        </button>
                                        <button onClick={() => del(d.id)}
                                                className="p-1 hover:bg-[var(--bg-surface-hover)] ml-1"
                                                data-testid={`did-delete-${d.id}`}>
                                            <Trash size={14} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>

            {editing && (
                <div className="fixed inset-0 z-50 flex items-center justify-center"
                     style={{ background: 'rgba(11,14,20,0.85)' }}>
                    <form onSubmit={save}
                          className="w-full max-w-lg border p-6"
                          style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)', borderRadius: 2 }}>
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.15em] font-bold"
                                     style={{ color: 'var(--brand)' }}>
                                    {editing.id ? 'Editace' : 'Nové DID'}
                                </div>
                                <h2 className="font-heading text-xl font-bold">DID Číslo</h2>
                            </div>
                            <button type="button" onClick={() => setEditing(null)}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            {[
                                ['number', 'Číslo (E.164)', 'tel'],
                                ['country', 'Země (ISO)', 'text'],
                                ['city', 'Město', 'text'],
                                ['label', 'Štítek', 'text'],
                                ['customer', 'Zákazník', 'text'],
                                ['department', 'Oddělení', 'text'],
                            ].map(([k, label, type]) => (
                                <div key={k}>
                                    <label className="text-[10px] uppercase tracking-[0.1em] font-bold"
                                           style={{ color: 'var(--text-secondary)' }}>{label}</label>
                                    <input
                                        type={type}
                                        value={editing[k] || ''}
                                        onChange={(e) => setEditing({ ...editing, [k]: e.target.value })}
                                        required={k === 'number'}
                                        className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm font-mono"
                                        style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}
                                    />
                                </div>
                            ))}
                            <div className="col-span-2">
                                <label className="text-[10px] uppercase tracking-[0.1em] font-bold"
                                       style={{ color: 'var(--text-secondary)' }}>Call Flow</label>
                                <select
                                    value={editing.assigned_call_flow_id || ''}
                                    onChange={(e) => setEditing({ ...editing, assigned_call_flow_id: e.target.value || null })}
                                    className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm"
                                    style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}
                                >
                                    <option value="">— žádný —</option>
                                    {flows.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                                </select>
                            </div>
                            <div className="col-span-2">
                                <label className="text-[10px] uppercase tracking-[0.1em] font-bold"
                                       style={{ color: 'var(--text-secondary)' }}>Poznámka</label>
                                <textarea
                                    rows={2}
                                    value={editing.notes || ''}
                                    onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                                    className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm"
                                    style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}
                                />
                            </div>
                            <label className="col-span-2 inline-flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={!!editing.is_active}
                                       onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} />
                                Aktivní
                            </label>
                        </div>

                        <div className="flex justify-end gap-2 mt-5">
                            <button type="button" onClick={() => setEditing(null)}
                                    className="px-3 py-2 text-xs font-bold uppercase border"
                                    style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>Zrušit</button>
                            <button type="submit" data-testid={TID.didSaveBtn}
                                    className="px-3 py-2 text-xs font-bold uppercase"
                                    style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>
                                Uložit
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </>
    );
};

export default Dids;
